import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';

import { fetchIntegrationHealth } from '@src/api/actions/dashboard/dataIntegrations';
import paths from '@src/paths';
import { useNavigate, useSearchParams } from 'react-router-dom';

const TYPE_LABELS = {
  email: 'Automated Email',
  sftp: 'SFTP',
  api: 'API',
  google_sheet: 'Google Sheet',
};

const STATUS_COLORS = {
  succeeded: 'success',
  failed: 'error',
  running: 'info',
};

function timeSince(isoString) {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function DataIntegrationHealthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (clientId) params.filter_client = clientId;
      const res = await fetchIntegrationHealth(params);
      setIntegrations(res.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load integration health.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Data Integrations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage your backend data connections.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate(paths.dataIntegrations.history)}
          >
            Import History
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadHealth}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(paths.dataIntegrations.wizard)}
          >
            New Connection
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : integrations.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              No data integrations configured yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Connect your CRM or backend data source to start importing leads, appointments, and sales.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(paths.dataIntegrations.wizard)}
            >
              Set Up Your First Connection
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {integrations.map((item) => (
            <Card key={`${item.integration_type}-${item.id}`} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {item.client_name || 'Unknown Client'}
                      </Typography>
                      <Chip
                        label={TYPE_LABELS[item.integration_type] || item.integration_type}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Schedule: {item.schedule_type || 'N/A'}
                      {item.run_time ? ` at ${item.run_time}` : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary">
                        Last import
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {timeSince(item.last_run_at)}
                      </Typography>
                    </Box>
                    <Chip
                      label={item.last_run_status || 'No runs'}
                      color={STATUS_COLORS[item.last_run_status] || 'default'}
                      size="small"
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
